import { Database, DatabaseProviderInformation, DatabaseResponse } from '@couchbasecloud/base-ui/types/database-service.types';
import { search as fuzzySearch } from 'fast-fuzzy';
import { listDatabases, listProjectDatabases } from 'sync/database-service';
import { PerPageValue, QueryOptions } from 'sync/query';
import { matchLeftCharacters } from 'utils/strings';

type GetEntireDatabaseListProps = {
  startPage: number;
  perPage: PerPageValue;
  organizationId: string;
  sortBy?: string;
  sortDirection?: QueryOptions['sortDirection'];
} & (
  | {
      isProjectDatabase: boolean;
      projectId: string;
    }
  | {}
);

/**
 * getEntireList fetches all databases. Only the RbacResponse of the
 * first page is kept. Subsequent pages have their data merged into the
 * RbacResponse returned by the first page.
 */
export const getEntireDatabaseList = async ({
  startPage,
  perPage,
  organizationId,
  sortBy,
  sortDirection,
  ...props
}: GetEntireDatabaseListProps) => {
  /**
   * Fetches projects with given page and organization identifier.
   */

  const isProjectDatabase = 'isProjectDatabase' in props ? props.isProjectDatabase : false;
  const projectId = 'projectId' in props ? props.projectId : undefined;

  const getPage = async (page: number) => {
    try {
      if (isProjectDatabase && projectId) {
        return await listProjectDatabases(
          { projectId, organizationId },
          {
            page,
            perPage,
            sortBy,
            sortDirection,
          }
        );
      }
      return await listDatabases({ organizationId }, { page, perPage, sortBy, sortDirection });
    } catch {
      return null;
    }
  };

  const getNextPageRecords = async ({
    currentResponse,
    currentPage,
  }: {
    currentResponse: Awaited<ReturnType<typeof getPage>>;
    currentPage: number;
  }): Promise<DatabaseResponse[]> => {
    if (!currentResponse?.data?.length) {
      return [];
    }

    const nextPage = currentPage + 1;
    const nextResponse = await getPage(nextPage);
    const nextResults = await getNextPageRecords({ currentResponse: nextResponse, currentPage: nextPage });

    return [...currentResponse.data, ...nextResults];
  };

  const startPageResponse = await getPage(startPage);

  if (!startPageResponse) {
    return null;
  }

  const nextPageRecords = await getNextPageRecords({
    currentResponse: startPageResponse,
    currentPage: startPage,
  });

  return {
    ...startPageResponse,
    data: nextPageRecords,
  };
};

type IncludeSearchSuggestionsProps = {
  databaseList: DatabaseResponse[];
  addUniqueResult: (item: DatabaseResponse, rank: number) => void;
  searchTerm: string;
  isVersionSearch: boolean;
};

export const includeSearchSuggestions = ({ databaseList, addUniqueResult, searchTerm, isVersionSearch }: IncludeSearchSuggestionsProps) => {
  // First, let's check to see if someone is searching for an exact match.
  databaseList.forEach((record) => {
    // If they are looking for a specific Couchbase Version, handle that.
    if (isVersionSearch) {
      if (record.data.config && record.data.config.version === searchTerm) {
        addUniqueResult(record, 1);
      }

      return;
    }

    // If the search term looks like they just want to list databases
    // then we can give them all the databases.
    if (matchLeftCharacters(searchTerm, ['databases'])) {
      addUniqueResult(record, 1);
      return;
    }

    if (matchLeftCharacters(searchTerm, ['serverless'])) {
      if (record.data.provider.deliveryMethod === 'serverless') {
        addUniqueResult(record, 1);
        return;
      }
    }

    if (matchLeftCharacters(searchTerm, ['hosted', 'provisioned'])) {
      if (record.data.provider.deliveryMethod === 'hosted') {
        addUniqueResult(record, 1);
        return;
      }
    }

    if (matchLeftCharacters(searchTerm, ['onprem'])) {
      if (record.data.provider.deliveryMethod === 'onPrem') {
        addUniqueResult(record, 1);
        return;
      }
    }

    // Check the ID?
    if (record.data.id.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
      return;
    }

    // Check the Project ID?
    if (record.data.project.id.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
      return;
    }

    // Check the Connection String?
    if (record.data.connect && record.data.connect.srv && record.data.connect.srv.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
      return;
    }

    // Check the version someone is looking for?
    if (record.data.version && record.data.version.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
    }
  });
};

export const getDbPrefix = (database: Database) => {
  let dbPrefix: string;

  switch (database.provider.deliveryMethod) {
    case 'hosted':
      dbPrefix = `Provisioned${database.config?.version ? ` ${database.config.version}` : ''} database`;
      break;
    case 'inVpc':
      dbPrefix = 'InVPC database';
      break;
    case 'onPrem':
      dbPrefix = 'On-prem database';
      break;
    case 'serverless':
      dbPrefix = 'Serverless database';
      break;
    default:
      dbPrefix = 'A database';
      break;
  }

  return dbPrefix;
};

export const getSearchKeySelectors = () => {
  return [
    // Add a selector to search the database name
    {
      boost: 2,
      fn: (record: DatabaseResponse) => {
        return record.data.name;
      },
    },
    // Add a selector to search the CSP
    {
      boost: 1,
      fn: (record: DatabaseResponse) => {
        const deliveryMethod = record.data.provider.deliveryMethod as unknown as DatabaseProviderInformation;

        if (deliveryMethod && deliveryMethod.name) {
          return deliveryMethod && deliveryMethod.name;
        }

        return '';
      },
    },
    // Add a selector to allow search of the provider and region
    {
      boost: 1,
      fn: (record: DatabaseResponse) => {
        const deliveryMethod = record.data.provider.deliveryMethod as unknown as DatabaseProviderInformation;

        if (deliveryMethod && deliveryMethod.region) {
          return deliveryMethod && deliveryMethod.region;
        }

        return '';
      },
    },
    // Add a selector to allow search of the individual who created
    {
      boost: 1,
      fn: (record: DatabaseResponse) => {
        if (record.data.createdBy) {
          return record.data.createdBy;
        }

        return '';
      },
    },
  ] as const;
};

type IncludeKeySelectorResultsProps = {
  databaseList: DatabaseResponse[];
  addUniqueResult: (item: DatabaseResponse, rank: number) => void;
  searchTerm: string;
  maxResults?: number;
  resultSet: Set<string>;
};

export const includeKeySelectorResults = ({
  addUniqueResult,
  databaseList,
  maxResults,
  resultSet,
  searchTerm,
}: IncludeKeySelectorResultsProps) => {
  getSearchKeySelectors().forEach((keySelector) => {
    // We may not need to perform further, less relevant searches...
    if (maxResults && resultSet.size >= maxResults) {
      return;
    }

    const matches = fuzzySearch(searchTerm, databaseList, {
      returnMatchData: true,
      keySelector: keySelector.fn,
    });

    matches.forEach((match) => {
      addUniqueResult(match.item, keySelector.boost + match.score);
    });
  });
};
